import { useEffect, useState, type ReactNode } from 'react';
import { Logo } from '@wyocspay/ui';
import type { PaymentView } from './api.js';
import { Assurance } from './Assurance.js';
import { LANGUAGES, type Lang, type Strings } from './i18n.js';
import { COMPANY, addressLine } from './legal.js';

/**
 * Page chrome for the whole payment flow, matching the WyoCSPay reference
 * design: a blue masthead, a session band with the transaction number and a
 * live countdown, a three-step wizard (Payment method -> Confirmation ->
 * Result), and a footer of trust facts. Built with inline styles on the brand
 * tokens, exactly like the reference; no external fonts or scripts, so the
 * strict CSP that keeps this page in PCI SAQ A is untouched.
 */

const MAXW = 1140;
const BLUE = '#3a6a8d';
const NAVY = '#24506f';
const INK = '#26313a';
const MUTED = '#5d6b74';
const FAINT = '#8a959c';
const LINE = '#d8dcdf';

const STEP_KEYS = ['method', 'confirm', 'result'] as const;

function fmt(sec: number): string {
  if (sec >= 3600) {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return `${h}:${String(m).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;
  }
  const m = Math.floor(sec / 60);
  return `${m}:${String(sec % 60).padStart(2, '0')}`;
}

export function Shell({
  lang,
  onLang,
  t,
  locale,
  merchant,
  merchantLogo,
  paymentId,
  assurance,
  chrome = 'full',
  step = 1,
  expiresAt = null,
  children,
  onOpenLegal,
}: {
  lang: Lang;
  onLang: (lang: Lang) => void;
  t: Strings;
  locale: string;
  merchant: string | null;
  merchantLogo: string | null;
  paymentId: string | null;
  assurance: PaymentView['assurance'] | null;
  /**
   * How much payment chrome to draw. 'full' is a live payment: the session
   * band, the 1-2-3 wizard, and the trust badges all belong. 'bare' is a page
   * with nothing to pay - the landing screen, an unusable link - where those
   * elements would be claiming a session that does not exist, so only the
   * masthead and the company footer remain.
   */
  chrome?: 'full' | 'bare';
  /** Which wizard step is active: 1 method, 2 confirmation, 3 result. */
  step?: 1 | 2 | 3;
  /**
   * The link's real expiry, ISO. The session band counts down to THIS fixed
   * moment, so a reload shows the true remaining time rather than restarting.
   * Null hides the countdown entirely.
   */
  expiresAt?: string | null;
  children: ReactNode;
  onOpenLegal: (doc: 'terms' | 'privacy') => void;
}) {
  // Optional reference-design strings, read leniently so no locale is forced to
  // define them; English fallbacks below cover any that a translation omits.
  const x = t as unknown as {
    stepMethod?: string; stepConfirm?: string; stepResult?: string;
    txnLabel?: string; sessionLabel?: string; sessionExpired?: string;
    trust3dsTitle?: string; trust3dsBody?: string; trustPciTitle?: string; trustPciBody?: string;
    trust247Title?: string; trust247Body?: string;
  };
  const stepLabels = [
    x.stepMethod ?? 'Payment method',
    x.stepConfirm ?? 'Confirmation',
    x.stepResult ?? 'Result',
  ];

  // Count down to the link's real expiry (a fixed server timestamp), so a reload
  // shows the true remaining time and never restarts from a fresh 10:00.
  const [left, setLeft] = useState<number | null>(null);
  useEffect(() => {
    if (!expiresAt) { setLeft(null); return; }
    const target = new Date(expiresAt).getTime();
    if (Number.isNaN(target)) { setLeft(null); return; }
    const tick = () => setLeft(Math.max(0, Math.floor((target - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#eceeef' }}>
      <style>{`
        .sp-h { padding: 16px 24px; }
        .sp-band { padding: 8px 24px; }
        .sp-main { padding: 26px 24px 60px; }
        .sp-wrap { max-width: ${MAXW}px; margin: 0 auto; width: 100%; box-sizing: border-box; }
        .sp-steps { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
        @media (max-width: 640px) {
          .sp-h { flex-direction: column; gap: 10px; text-align: center; }
          .sp-h-nav { justify-content: center; }
          .sp-h-right { justify-content: center; }
          .sp-band { flex-direction: column; gap: 3px; text-align: center; }
          .sp-steps { grid-template-columns: 1fr; gap: 8px; }
          .sp-foot-grid { grid-template-columns: 1fr !important; }
          .sp-foot-grid > div { flex-direction: column !important; align-items: center !important; text-align: center; gap: 8px; }
          .sp-foot-bottom { flex-direction: column; align-items: center; text-align: center; }
          .sp-foot-links { justify-content: center; width: 100%; }
        }
      `}</style>

      {/* Masthead */}
      <header style={{ background: BLUE, color: '#fff' }}>
        <div className="sp-wrap sp-h" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 26, flexWrap: 'wrap' }}>
            <Logo height={56} />
          </div>
          <div className="sp-h-right" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13.5, color: '#dbe9f2' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#5cd08a', display: 'inline-block' }} />
              {t.secure}
            </span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="wc-sr-only">{t.language}</span>
              <select
                value={lang}
                onChange={(e) => onLang(e.target.value as Lang)}
                aria-label={t.language}
                style={{ padding: '5px 9px', fontSize: 12.5, fontWeight: 600, border: '1px solid rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.12)', color: '#fff', cursor: 'pointer', borderRadius: 3 }}
              >
                {LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code} style={{ color: INK }}>{l.label}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {/* Session band - only while an actual payment session exists. */}
        {chrome === 'full' ? (
          <div style={{ background: NAVY }}>
            <div className="sp-wrap sp-band" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', fontSize: 13.5, color: '#bcd4e4' }}>
              <span>
                {x.txnLabel ?? 'Transaction no.'}{' '}
                <span className="wc-num" style={{ color: '#e6eef4' }}>{paymentId ?? '-'}</span>
              </span>
              {left !== null ? (
                <span>
                  {left <= 0
                    ? (x.sessionExpired ?? 'Session expired')
                    : <>{x.sessionLabel ?? 'Session expires in'}{' '}<span className="wc-num" style={{ color: left <= 60 ? '#f0b34a' : '#e6eef4', fontWeight: 700 }}>{fmt(left)}</span></>}
                </span>
              ) : <span />}
            </div>
          </div>
        ) : null}
      </header>

      {/* Wizard - only while an actual payment session exists. */}
      {chrome === 'full' ? (
      <div className="sp-wrap" style={{ paddingLeft: 24, paddingRight: 24, boxSizing: 'border-box', marginTop: 22 }}>
        <div className="sp-steps">
          {STEP_KEYS.map((_, i) => {
            const n = (i + 1) as 1 | 2 | 3;
            const active = n === step;
            const done = n < step;
            return (
              <div
                key={n}
                aria-current={active ? 'step' : undefined}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px',
                  background: active ? '#fff' : '#e6e9eb',
                  border: `1px solid ${active ? LINE : '#dde1e3'}`,
                  borderBottom: active ? `3px solid ${NAVY}` : `1px solid #dde1e3`,
                  boxSizing: 'border-box',
                }}
              >
                <span style={{
                  width: 26, height: 26, borderRadius: '50%', flex: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700,
                  background: active || done ? NAVY : '#c3ccd1',
                  color: '#fff',
                }}>{done ? '✓' : n}</span>
                <span style={{ fontSize: 15, fontWeight: active ? 700 : 600, color: active ? INK : '#8794a0' }}>
                  {stepLabels[i]}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      ) : null}

      <main className="sp-wrap sp-main" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {children}
      </main>

      {/* Trust footer */}
      <footer style={{ borderTop: `1px solid ${LINE}`, background: '#f4f6f7' }}>
        <div className="sp-wrap" style={{ padding: '22px 24px' }}>
          {/* Trust badges - only while an actual payment session exists. */}
          {chrome === 'full' ? (
            <div className="sp-foot-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 22 }}>
              <TrustBadge tag="3DS" title={x.trust3dsTitle ?? '3-D Secure 2.2'} body={x.trust3dsBody ?? 'Every charge is authenticated directly with your issuing bank.'} />
              <TrustBadge tag="PCI" title={x.trustPciTitle ?? 'PCI DSS Level 1'} body={x.trustPciBody ?? 'Card data is tokenized and never stored on our servers.'} />
              <TrustBadge tag="24/7" title={x.trust247Title ?? 'Around-the-clock support'} body={x.trust247Body ?? 'Our team answers every transaction inquiry within two hours.'} />
            </div>
          ) : null}

          <div className="sp-foot-bottom" style={{ marginTop: chrome === 'full' ? 20 : 0, paddingTop: chrome === 'full' ? 16 : 0, borderTop: chrome === 'full' ? `1px solid ${LINE}` : 'none', display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', fontSize: 12.5, color: FAINT }}>
            <div style={{ maxWidth: 520 }}>
              <div style={{ color: MUTED, fontWeight: 600 }}>{COMPANY.legalName}</div>
              <div>{addressLine}</div>
              <div>{COMPANY.phone} &middot; {COMPANY.email}</div>
              <div style={{ marginTop: 4 }}>&copy; {new Date().getFullYear()} &middot; {t.rights}</div>
              {assurance ? <div style={{ marginTop: 6 }}><Assurance t={t} locale={locale} assurance={assurance} /></div> : null}
            </div>
            <div className="sp-foot-links" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <button type="button" onClick={() => onOpenLegal('terms')} style={footLink}>{t.terms}</button>
              <button type="button" onClick={() => onOpenLegal('privacy')} style={footLink}>{t.privacy}</button>
              <a href={`mailto:${COMPANY.email}`} style={{ color: '#2f6188' }}>{t.support}</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

const footLink: React.CSSProperties = { background: 'none', border: 'none', padding: 0, font: 'inherit', color: '#2f6188', textDecoration: 'underline', cursor: 'pointer' };

function TrustBadge({ tag, title, body }: { tag: string; title: string; body: string }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <span style={{ flex: 'none', minWidth: 40, height: 30, padding: '0 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: NAVY, color: '#fff', fontSize: 12, fontWeight: 700, letterSpacing: '0.04em' }}>{tag}</span>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: INK }}>{title}</div>
        <div style={{ fontSize: 12.5, color: MUTED, lineHeight: 1.45, marginTop: 2 }}>{body}</div>
      </div>
    </div>
  );
}

/** A white card with the reference's header-strip style. Reused across screens. */
export function Panel({ title, children, headAccent }: { title?: string; children: ReactNode; headAccent?: boolean }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${LINE}`, boxShadow: 'rgba(38,49,58,0.05) 0px 1px 2px' }}>
      {title ? (
        <div style={{
          padding: '13px 26px',
          borderBottom: `1px solid #e2e5e8`,
          fontSize: 12, letterSpacing: '0.09em', textTransform: 'uppercase',
          color: headAccent ? '#fff' : MUTED,
          background: headAccent ? INK : '#f7f8f9',
        }}>{title}</div>
      ) : null}
      <div style={{ padding: '22px 26px 26px' }}>{children}</div>
    </div>
  );
}

export const PAY_TOKENS = { MAXW, BLUE, NAVY, INK, MUTED, FAINT, LINE };
